import { apiGet, apiPost, apiRequest } from './client';

export interface ForwardingRule {
  address: string;
  destination: string | null;
  enabled: boolean;
  ruleTag: string | null;
  managed: boolean;
  createdBy: string | null;
  status?: string;
}

export interface EmailForwardingStatus {
  domain: string;
  zoneId?: string;
  routing: { enabled: boolean | null; status: string };
  rules: ForwardingRule[];
}

export interface ForwardResult {
  address: string;
  destination: string;
  outcome: 'active' | 'pending-verification' | 'exists' | 'failed';
  detail?: string;
}

export interface SetupResult {
  domain: string;
  routing: { enabled: boolean | null; status: string };
  mxConflicts: string[];
  results: ForwardResult[];
  pendingVerification: string[];
  summary: string;
}

/** Reconciled view of a domain's forwarding rules (Cloudflare is source of truth). */
export function getEmailForwardingStatus(domain: string): Promise<EmailForwardingStatus> {
  return apiGet<EmailForwardingStatus>(
    `/api/admin/email-forwarding/status?domain=${encodeURIComponent(domain)}`
  );
}

/** Create a single forward: `${localPart}@${domain}` → `destination`. */
export function addEmailForward(
  domain: string,
  localPart: string,
  destination: string
): Promise<SetupResult> {
  return apiPost<SetupResult>('/api/admin/email-forwarding/setup', {
    domain,
    forwards: [{ localPart, destination }],
    createdBy: 'dashboard',
  });
}

/** Remove a forward by local part (deletes the Cloudflare rule, marks D1 disabled). */
export function removeEmailForward(
  domain: string,
  localPart: string
): Promise<{ domain: string; address: string; deleted: boolean }> {
  return apiRequest('/api/admin/email-forwarding/rule', {
    method: 'DELETE',
    body: JSON.stringify({ domain, localPart }),
  });
}

export interface DisableResult {
  domain: string;
  zoneId: string;
  routing: { enabled: boolean | null; status: string };
  suspended: number;
  summary: string;
}

/**
 * Pause Email Routing for the whole domain: turns routing off (forwarding stops,
 * MX/SPF unlocked) and saves the current addresses as 'suspended'. Re-enable
 * restores them.
 */
export function disableEmailRouting(domain: string): Promise<DisableResult> {
  return apiPost<DisableResult>('/api/admin/email-forwarding/disable', { domain });
}

export interface EnableResult {
  domain: string;
  zoneId: string;
  routing: { enabled: boolean | null; status: string };
  restored: number;
  summary: string;
}

/**
 * Re-enable Email Routing for the domain and restore every previously-suspended
 * address (routing back on + rules re-created as needed).
 */
export function enableEmailRouting(domain: string): Promise<EnableResult> {
  return apiPost<EnableResult>('/api/admin/email-forwarding/enable', { domain });
}
